import { useEffect } from 'react';
import { fetchAddressFromOla } from '../services/fetchAddressfromOla';

const useInitializeMap = ({
  showModal,
  uploadedGeoJSONLayer,
  defaultValue,
  selectedLocation,
  setInitializing,
  setIsLoading,
  setUserLocation,
  setSelectedLocation,
  setAddressDetails,
  setShowLocationError,
  getGeoJSONCenter,
  apiKey
}) => {
  const setCenterFromGeoJSON = () => {
    if (!uploadedGeoJSONLayer) return false;

    const center = getGeoJSONCenter(uploadedGeoJSONLayer);
    if (!center) return false;

    setUserLocation(center);
    setSelectedLocation(center);
    return true;
  };

  const setCenterFromDefault = () => {
    const location = selectedLocation || defaultValue;
    if (!location?.lat) return false;

    setUserLocation(location);
    setSelectedLocation(location);
    return true;
  };

  const getCurrentPosition = abortController =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 8000,
        maximumAge: 0
      });

      abortController.signal.addEventListener('abort', () => {
        reject(new DOMException('Aborted', 'AbortError'));
      });
    });

  const fetchAndSetAddress = async (lat, lng, abortController) => {
    try {
      const details = await fetchAddressFromOla(lat, lng, apiKey, {
        signal: abortController.signal
      });

      setSelectedLocation(prev => ({
        ...prev,
        address: details?.address || 'Address not available'
      }));
      setAddressDetails(details);
    } catch (err) {
      console.error('Address fetch error:', err);
      setSelectedLocation(prev => ({
        ...prev,
        address: 'Address fetch failed'
      }));
    }
  };

  const handleGeolocation = async abortController => {
    const position = await getCurrentPosition(abortController);
    const { latitude: lat, longitude: lng } = position.coords;
    const locationData = { lat, lng };

    setUserLocation(locationData);
    setSelectedLocation(locationData);

    await fetchAndSetAddress(lat, lng, abortController);
  };

  const initializeLocation = async abortController => {
    // Try GeoJSON center first
    if (setCenterFromGeoJSON()) return;

    // Try default/selected location second
    if (setCenterFromDefault()) return;

    // Fall back to geolocation
    await handleGeolocation(abortController);
  };

  // FIXED: Removed parameter shadowing by eliminating the helper function
  // and directly using the outer scope variables
  const runInitialization = async abortController => {
    setInitializing(true);
    setIsLoading(true);
    try {
      await initializeLocation(abortController);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Location error:', error);
        setShowLocationError(true);
      }
    } finally {
      setInitializing(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!showModal) return;

    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), 5000);

    // FIXED: Direct function call without parameter shadowing
    runInitialization(abortController);

    return () => {
      abortController.abort();
      clearTimeout(timeoutId);
    };
  }, [showModal]);
};

export default useInitializeMap;
